import { Box, Button, Flex, Grid, GridItem, Text } from '@everlywell/ui-kit';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import ApplePayWidget from 'components/ApplePayWidget';
import { useCart } from 'components/Cart';
import { DisclaimerDataProps } from 'components/GeneralDisclaimer';
import { ScreenerDataProps } from 'components/PrePurchaseScreener';
import Price from 'components/Price';
import { usePurchaseOptionsContext } from 'components/PurchaseOptionsDrawerProvider';
import { PurchaseOptionsDrawerProduct } from 'components/PurchaseOptionsDrawerProvider/PurchaseOptionsDrawerProvider.context';
import StripePayment, {
  StripePaymentProps,
} from 'components/Stripe/StripePayment';
import StripeMerchandisingWidget from 'components/StripeMerchandisingWidget';
import { useToastContext } from 'components/ToastContext/ToastContext';
import {
  PrePurchaseModalActionsTypes,
  usePrePurchaseModalContext,
} from 'contexts/PrePurchaseModal';
import { navigate } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import React, { useCallback, useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { processExpressPayOrder } from 'utils/api/cartApis';
import { ANALYTICS } from 'utils/constants/analytics';
import { CHECKOUT_QUEST_BASE_PATH } from 'utils/constants/checkout';
import { VITAMINS } from 'utils/constants/products';
import { logError } from 'utils/helpers';
import { discountedPrice, Promo } from 'utils/helpers/activePromoHelpers';
import { stripePublicKey } from 'utils/helpers/paymentHelpers';
import { notEmpty } from 'utils/types/utilityTypes';

import { useSelectedOptionsContext } from '../../SelectedOption.context';
import IncentivesList from '../IncentivesList';

export type MobilePurchaseOptionsProps = {
  product: Queries.pdpSpreeProductFragment;
  isProductOutOfStock?: boolean;
  isPreSelectedSubscription?: boolean;
  hasPrePurchaseStep?: boolean;
  screener?: ScreenerDataProps;
  disclaimer?: DisclaimerDataProps;
  isQuestProduct?: boolean;
  contentfulFlags?: Queries.pdpContentfulMetadata001Fragment['flags'];
  showPromo?: boolean;
  activePromo: Promo | null;
  skipPreCart?: boolean;
};

/**
 * [Add the description for what this component does here].
 */
const MobilePurchaseOptions = (props: MobilePurchaseOptionsProps) => {
  const {
    product,
    isProductOutOfStock,
    isPreSelectedSubscription,
    hasPrePurchaseStep,
    screener,
    disclaimer,
    isQuestProduct,
    contentfulFlags,
    showPromo,
    activePromo,
    skipPreCart,
  } = props;
  const { variants, master_variant_id: productVariantId, sku } = product;
  const prePurchaseModalContext = usePrePurchaseModalContext();
  const selectedOptionContext = useSelectedOptionsContext();

  const { selectedOption } = selectedOptionContext;

  const { enableStripeUpgrade } = useFlags();

  // ### Stripe

  const [hasStripeError, setHasStripeError] = useState(false);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  const handleStripeLoadingError = useCallback((hasError: boolean) => {
    setHasStripeError(hasError);
  }, []);

  useEffect(() => {
    if (!stripePromise) {
      setStripePromise(loadStripe(stripePublicKey));
    }
  }, [stripePromise]);

  // determine Stripe button height based off of the height of the adjacent button
  const ref = React.useRef<HTMLButtonElement>(null);
  const [buttonHeight, setButtonHeight] = useState<number | null>(null);
  useEffect(() => {
    if (ref.current) {
      setButtonHeight(ref.current.clientHeight);
    }
  }, []);

  const productVariant = variants?.find(
    (variant) => variant?.id === `${productVariantId ?? 0}`,
  );

  const isVitamin = VITAMINS.includes(product.slug ?? '');

  const subscriptionVariants =
    variants
      ?.filter((variant) => variant?.subscription_plan)
      .filter(notEmpty) ?? [];

  const subscriptionPrice = Number(subscriptionVariants[0]?.price ?? '0');

  const subscriptionDiscount =
    subscriptionVariants[0]?.subscription_plan?.percent_discount ?? 0;

  // ### Cart interactions

  const { setProduct } = usePurchaseOptionsContext();

  const purchaseDrawerProduct: PurchaseOptionsDrawerProduct = {
    name: props.product.name ?? '',
    slug: props.product.slug ?? '',
    variantId: props.product.master_variant_id ?? 0,
    price: productVariant?.price ?? '0',
    subscriptionVariants: (subscriptionVariants ?? []).map((variant) => ({
      id: Number(variant?.id ?? '0'),
      name: variant?.subscription_plan?.name ?? '',
      unit: variant?.subscription_plan?.interval ?? '',
      price: variant?.price ?? '0',
      price_in_dollars: variant?.price ?? '0',
      is_master: variant?.is_master ?? false,
      sku: variant?.sku ?? '',
      subscription_plan: variant?.subscription_plan
        ? {
            id: variant?.subscription_plan?.id ?? 0,
            name: variant?.subscription_plan?.name ?? '',
            interval: variant?.subscription_plan?.interval ?? '',
            interval_count: variant?.subscription_plan?.interval_count ?? 0,
            percent_discount: variant?.subscription_plan?.percent_discount ?? 0,
          }
        : undefined,
    })),
    hideSubscription: false,
    preSelectedSubscription: isPreSelectedSubscription,
    productId: props.product.productId ?? 0,
    promotionable: props.product.promotionable ?? false,
  };

  // ### Cart interactions
  const { addItemToCartAsync, isAddItemToCartLoading } = useCart();

  const handleAddToCart = async ({ variantId }: { variantId: number }) => {
    try {
      await addItemToCartAsync({
        variant_id: variantId,
        quantity: 1,
      });
    } catch (error) {
      logError('There was an issue adding the item to the cart', {
        component: 'MobilePurchaseOptions',
        method: 'handleAddToCart',
        experience: 'PDP',
      });
    }
  };

  const handleAddToCartClick = async () => {
    if (isQuestProduct) {
      analytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: ANALYTICS.LABELS.QUEST_BUY_TEST,
          component: 'MobilePurchaseOptions',
          product: product.name ?? '',
        },
      });
      navigate(`${CHECKOUT_QUEST_BASE_PATH}/?variant=${productVariantId}`);
      return;
    }
    if (hasPrePurchaseStep) {
      if (hasPrePurchaseStep) {
        prePurchaseModalContext?.dispatch({
          type: PrePurchaseModalActionsTypes.SetPrePurchaseScreener,
          payload: {
            isPrePurchaseScreenerOpen: true,
            prePurchaseScreenerData: screener,
            generalDisclaimerData: disclaimer,
          },
        });
      }
    }

    if (skipPreCart) {
      await handleAddToCart({ variantId: purchaseDrawerProduct?.variantId });
      return;
    }

    if (!isProductOutOfStock && !hasPrePurchaseStep) {
      setProduct(purchaseDrawerProduct);
    }
  };

  // ### Pricing

  const oneTimePurchasePrice = Number(productVariant?.price ?? '0');
  const oneTimePurchaseDiscountedPrice = activePromo
    ? parseFloat(discountedPrice(oneTimePurchasePrice, activePromo))
    : undefined;
  const showPromotion =
    Boolean(activePromo) &&
    oneTimePurchaseDiscountedPrice !== undefined &&
    showPromo;

  const displayPrice = isVitamin
    ? subscriptionPrice
    : showPromotion
    ? oneTimePurchaseDiscountedPrice
    : oneTimePurchasePrice;

  const displayPriceBeforeDiscount = oneTimePurchasePrice;

  // ### Express Pay
  const toast = useToastContext();
  // express pay total is in cents
  // note: express pay only works for one time purchase, but regardless if it changes in the future, we should charge what the user sees
  const expressPayTotal = displayPrice * 100;

  /* unable to test stripe in our unit tests at the moment */
  /* istanbul ignore next */
  const handleExpressPayPaymentConfirm: StripePaymentProps['handlePaymentConfirm'] =
    async (data, token) => {
      try {
        if (productVariantId) {
          const response = await processExpressPayOrder({
            type: 'singleItem',
            variantId: productVariantId,
            promoCode: showPromotion
              ? activePromo?.code ?? undefined
              : undefined,
            data,
            token,
          });

          navigate(
            `/confirmation?orderId=${response.data.id}&registration_token=${response.data.registration_token}&referral=true`,
          );
        }
      } catch (e: any) {
        const error = e as unknown as Error;
        let errorMessage =
          'Oops! Something went wrong. Please try again or contact support for help';
        if (!/(network|syntax)/i.test(error.message)) {
          errorMessage = error.message;
        }
        toast.showToast({
          message: errorMessage,
          status: 'error',
        });

        logError('Error processing express pay payment', {
          component: 'PurchaseOptions',
          method: 'handleExpressPayPaymentConfirm',
          experience: 'PDP',
        });
      }
    };

  return (
    <Grid spacing={4}>
      <GridItem width={'100%'}>
        <Flex flexDirection="row" justifyContent="center" alignItems="baseline">
          {showPromotion || isPreSelectedSubscription ? (
            <Flex gap={3} alignItems="baseline">
              <Price
                price={displayPriceBeforeDiscount}
                fontSize="3xl"
                color="tints.uiGrey"
                strikeThrough
              />

              <Box color={'utility.sale'}>
                <Price price={displayPrice} fontSize="3xl" />
              </Box>

              <Flex
                alignItems="baseline"
                gap={isPreSelectedSubscription ? 1 : 0.5}
              >
                <Text fontSize="xs" color="utility.sale">
                  {isPreSelectedSubscription ? 'Save' : 'with code'}
                </Text>

                <Text fontSize="md" color="utility.sale">
                  {isPreSelectedSubscription
                    ? `${subscriptionDiscount}%`
                    : activePromo?.code?.toUpperCase()}
                </Text>
              </Flex>
            </Flex>
          ) : (
            <Price price={displayPrice} fontSize="3xl" />
          )}
        </Flex>
      </GridItem>
      <GridItem width={'100%'}>
        <Flex flexDirection={'row'} justifyContent={'space-between'} gap={2}>
          <Box flex={1}>
            <Button
              onClick={handleAddToCartClick}
              isDisabled={isProductOutOfStock}
              data-testid="mobileAddToCartButton"
              width={'100%'}
              isLoading={isAddItemToCartLoading}
            >
              {isProductOutOfStock
                ? 'Out of stock'
                : isQuestProduct
                ? 'Buy test'
                : 'Add to cart'}
            </Button>
          </Box>

          {!isProductOutOfStock &&
            !hasStripeError &&
            selectedOption === 'oneTimePurchase' &&
            !isQuestProduct && (
              <Box flex={1}>
                <Elements stripe={stripePromise}>
                  <StripePayment
                    buttonHeight={buttonHeight}
                    cartTotal={expressPayTotal}
                    label={ANALYTICS.LABELS.EXPRESS_PAY_PDP}
                    variantsSkus={sku ? [sku] : []}
                    onStripeLoadingError={handleStripeLoadingError}
                    withApplePayWidget={false}
                    handlePaymentConfirm={handleExpressPayPaymentConfirm}
                  />
                </Elements>
              </Box>
            )}
        </Flex>
        {!isProductOutOfStock &&
        enableStripeUpgrade &&
        !isQuestProduct &&
        !isVitamin ? (
          <Flex marginTop={4} justifyContent="center">
            <StripeMerchandisingWidget
              amount={expressPayTotal}
              fontSizeBase="12px"
            />
          </Flex>
        ) : null}
        <Flex marginTop={3}>
          <ApplePayWidget
            amount={displayPriceBeforeDiscount.toFixed(0) ?? '0'}
          />
        </Flex>
      </GridItem>

      <GridItem>
        <IncentivesList contentfulFlags={contentfulFlags} />
      </GridItem>
    </Grid>
  );
};

export default MobilePurchaseOptions;
